/**
 * useDesignTokens — reads radius, font, and button-style design tokens
 * from the active design settings and returns ready-to-use style values.
 */
import { useDesignState } from '@/hooks/useDesignSystem';
import { RADIUS_OPTIONS, FONT_OPTIONS } from '@/pages/admin/components/design/designConstants';

export interface DesignTokens {
  /** CSS border-radius value e.g. "12px" */
  radius: string;
  /** Half of radius for inner elements */
  radiusSm: string;
  /** Full pill radius */
  radiusFull: string;
  /** CSS font-family string */
  font: string;
  /** button style id: 'filled' | 'outlined' | 'soft' */
  buttonStyle: string;
  /** card style id: 'shadow' | 'border' | 'flat' | 'glass' */
  cardStyle: string;
}

function halveRadius(val: string): string {
  if (val === '9999px') return '9999px';
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  return `${Math.max(4, Math.round(num / 2))}px`;
}

export function useDesignTokens(): DesignTokens {
  const { design } = useDesignState();

  const radiusOpt = RADIUS_OPTIONS.find(r => r.id === (design.radiusId ?? 'md')) ?? RADIUS_OPTIONS[2];
  const fontOpt   = FONT_OPTIONS.find(f => f.id === (design.fontId ?? 'cairo')) ?? FONT_OPTIONS[0];

  return {
    radius:      radiusOpt.value,
    radiusSm:    halveRadius(radiusOpt.value),
    radiusFull:  '9999px',
    font:        fontOpt.css,
    buttonStyle: design.buttonStyleId ?? 'filled',
    cardStyle:   design.cardStyleId ?? 'border',
  };
}
